import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DeviceIcon from '../DeviceIcon';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ClockIcon,
  ServerIcon,
  ChartBarIcon,
  CalendarIcon,
  SignalIcon,
  CommandLineIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  BoltIcon,
  CloudArrowUpIcon,
  MapPinIcon,
  WifiIcon,
  GlobeAltIcon,
  PlayIcon,
  StopIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import axiosInstance from '../../utils/axios';
import { useNavigate } from 'react-router-dom';

const UserSessionActivity = ({ userId, onStatsUpdate, devices = [] }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [expandedSessions, setExpandedSessions] = useState({});
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const limit = 10;

  useEffect(() => {
    fetchUserActivity();
  }, [userId, page]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setPage(0);
    setExpandedSessions({});
    try {
      const response = await axiosInstance.get(`/user-activity/${userId}?limit=${limit}&skip=0`);
      if (response.data.success) {
        const { sessions: newSessions, stats, pagination } = response.data.data;
        setSessions(newSessions);
        setStats(stats);
        setHasMore(pagination.hasMore);
        setLastRefreshed(new Date());
        if (onStatsUpdate) {
          onStatsUpdate(stats);
        }
      }
    } catch (err) {
      console.error('Failed to refresh user activity:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchUserActivity = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/user-activity/${userId}?limit=${limit}&skip=${page * limit}`);

      if (response.data.success) {
        const { sessions: newSessions, stats, pagination } = response.data.data;

        if (page === 0) {
          setSessions(newSessions);
          setStats(stats);
          if (onStatsUpdate) {
            onStatsUpdate(stats);
          }
        } else {
          setSessions(prev => [...prev, ...newSessions]);
        }

        setHasMore(pagination.hasMore);
      }
    } catch (err) {
      console.error('Failed to fetch user activity:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSession = (sessionId) => {
    setExpandedSessions(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };

  const getActionIcon = (actionType) => {
    const icons = {
      'update_api': CloudArrowUpIcon,
      'terminal_session': CommandLineIcon,
      'reboot_command': BoltIcon,
      'session_refresh': ArrowPathIcon,
      'update_value': DocumentTextIcon
    };
    return icons[actionType] || ChartBarIcon;
  };

  const formatActionType = (type) => {
    const labels = {
      'update_api': 'Settings Update',
      'terminal_session': 'Terminal Session',
      'reboot_command': 'Device Reboot',
      'session_refresh': 'Session Refresh',
      'update_value': 'Value Update'
    };
    return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getDisconnectionBadge = (reason) => {
    const badges = {
      'normal': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircleIcon, label: 'Clean Exit' },
      'navigation': { bg: 'bg-blue-100', text: 'text-blue-700', icon: GlobeAltIcon, label: 'Navigated Away' },
      'browser_close': { bg: 'bg-purple-100', text: 'text-purple-700', icon: XCircleIcon, label: 'Browser Closed' },
      'heartbeat_timeout': { bg: 'bg-orange-100', text: 'text-orange-700', icon: ExclamationTriangleIcon, label: 'Connection Lost' },
      'network_error': { bg: 'bg-red-100', text: 'text-red-700', icon: WifiIcon, label: 'Network Error' },
      'server_shutdown': { bg: 'bg-gray-100', text: 'text-gray-700', icon: ServerIcon, label: 'Server Restart' }
    };
    return badges[reason] || { bg: 'bg-gray-100', text: 'text-gray-600', icon: StopIcon, label: reason?.replace(/_/g, ' ') || 'Disconnected' };
  };

  const getConnectionQualityColor = (metrics) => {
    if (!metrics) return 'text-gray-400';
    if (metrics.heartbeatsMissed > 2) return 'text-red-500';
    if (metrics.heartbeatsMissed > 0) return 'text-orange-500';
    return 'text-green-500';
  };

  if (loading && page === 0) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-gray-100 h-32 rounded-xl"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <p className="text-red-600">Failed to load user activity: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Session Activity</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Last updated {formatDistanceToNow(lastRefreshed, { addSuffix: true })}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Statistics Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            whileHover={{ y: -2 }}
            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500 rounded-lg">
                <CalendarIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Sessions</p>
                <p className="text-2xl font-bold text-blue-900">{stats.totalSessions}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500 rounded-lg">
                <DeviceIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Devices</p>
                <p className="text-2xl font-bold text-green-900">{stats.totalDevices}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border border-purple-200"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Actions</p>
                <p className="text-2xl font-bold text-purple-900">{stats.totalActions}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-5 border border-orange-200"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-500 rounded-lg">
                <ClockIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-orange-600 uppercase tracking-wide">Time</p>
                <p className="text-2xl font-bold text-orange-900">{formatDuration(stats.totalDuration)}</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Activity Timeline */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ClockIcon className="w-5 h-5 text-gray-500" />
          Activity Timeline
        </h3>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-300 via-gray-200 to-transparent"></div>

          <div className="space-y-4">
            {sessions.map((session, idx) => {
              const isExpanded = expandedSessions[session.sessionId];
              const disconnectionBadge = getDisconnectionBadge(session.userDisconnectionReason);
              const DisconnectionIcon = disconnectionBadge.icon;

              return (
                <motion.div
                  key={session.sessionId}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="relative pl-14"
                >
                  {/* Timeline dot */}
                  <div className={`absolute left-4 top-6 w-5 h-5 rounded-full border-2 border-white shadow-md flex items-center justify-center ${
                    session.isActive
                      ? 'bg-green-500'
                      : session.userDisconnectionReason === 'heartbeat_timeout' || session.userDisconnectionReason === 'network_error'
                        ? 'bg-orange-400'
                        : 'bg-blue-400'
                  }`}>
                    {session.isActive && (
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    )}
                  </div>

                  <div className={`bg-white rounded-xl border overflow-hidden transition-all hover:shadow-md ${
                    session.isActive ? 'border-green-300 shadow-green-100' : 'border-gray-200'
                  }`}>
                    {/* Session Header - Clickable */}
                    <div
                      onClick={() => toggleSession(session.sessionId)}
                      className={`p-4 cursor-pointer transition-colors ${
                        session.isActive
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50'
                          : 'bg-gradient-to-r from-gray-50 to-slate-50 hover:from-gray-100 hover:to-slate-100'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        {/* Left side - Device info and time */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/devices/${session.deviceSerial}`);
                              }}
                              className="group flex items-center gap-1.5 font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                            >
                              <DeviceIcon className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                              <span className="truncate group-hover:underline underline-offset-2">
                                {session.deviceSerial}
                              </span>
                              <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
                            </button>
                            <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">
                              {session.deviceModel || 'DM2500i'}
                            </span>
                          </div>

                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-gray-500 flex items-center gap-1">
                              <ClockIcon className="w-3.5 h-3.5" />
                              {safeFormatDistanceToNow(session.sessionStarted, { addSuffix: true })}
                            </span>
                            <span className="text-gray-300">•</span>
                            <span className="text-gray-400 text-xs">
                              {safeFormat(session.sessionStarted, 'MMM d, h:mm a')}
                            </span>
                          </div>
                        </div>

                        {/* Right side - Status and duration */}
                        <div className="flex items-center gap-3">
                          {session.isActive ? (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full">
                              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                              <span className="text-sm font-medium">Active Now</span>
                            </div>
                          ) : (
                            <div className="text-right">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-semibold text-gray-700">
                                  {session.userActivity.durationFormatted}
                                </span>
                                <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${disconnectionBadge.bg} ${disconnectionBadge.text}`}>
                                  <DisconnectionIcon className="w-3 h-3" />
                                  {disconnectionBadge.label}
                                </div>
                              </div>

                              {/* Connection quality indicator */}
                              {session.connectionMetrics && (
                                <div className={`flex items-center gap-1 text-xs ${getConnectionQualityColor(session.connectionMetrics)}`}>
                                  <WifiIcon className="w-3 h-3" />
                                  {session.connectionMetrics.heartbeatsReceived} heartbeats
                                  {session.connectionMetrics.heartbeatsMissed > 0 && (
                                    <span className="text-red-500">
                                      ({session.connectionMetrics.heartbeatsMissed} missed)
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          <motion.div
                            animate={{ rotate: isExpanded ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                          </motion.div>
                        </div>
                      </div>

                      {/* Quick stats row */}
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <SignalIcon className="w-3.5 h-3.5" />
                          {session.userActivity.subscription?.topics?.join(', ') || 'No subscription'}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <ChartBarIcon className="w-3.5 h-3.5" />
                          {session.userActivity.actionsCount} action{session.userActivity.actionsCount !== 1 ? 's' : ''}
                        </div>
                        {session.deviceLocation?.formatted && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <MapPinIcon className="w-3.5 h-3.5" />
                            {session.deviceLocation.formatted}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expandable Details */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 border-t border-gray-100 bg-gray-50/50">
                            {/* Detail cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <p className="text-xs text-gray-500 mb-1">Started</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {safeFormat(session.sessionStarted, 'h:mm:ss a')}
                                </p>
                                <p className="text-xs text-gray-400">
                                  {safeFormat(session.sessionStarted, 'MMM d, yyyy')}
                                </p>
                              </div>

                              {session.sessionEnded && isValidDate(session.sessionEnded) && (
                                <div className="bg-white rounded-lg p-3 border border-gray-100">
                                  <p className="text-xs text-gray-500 mb-1">Ended</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {safeFormat(session.sessionEnded, 'h:mm:ss a')}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {safeFormat(session.sessionEnded, 'MMM d, yyyy')}
                                  </p>
                                </div>
                              )}

                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <p className="text-xs text-gray-500 mb-1">Device IP</p>
                                <p className="text-sm font-medium text-gray-900 font-mono">
                                  {session.deviceIp || 'Unknown'}
                                </p>
                              </div>

                              {session.userActivity.subscription?.ip && (
                                <div className="bg-white rounded-lg p-3 border border-gray-100">
                                  <p className="text-xs text-gray-500 mb-1">User IP</p>
                                  <p className="text-sm font-medium text-gray-900 font-mono">
                                    {session.userActivity.subscription.ip}
                                  </p>
                                  {session.userLocation?.formatted && (
                                    <p className="text-xs text-gray-400 mt-0.5">
                                      {session.userLocation.formatted}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Actions list */}
                            {session.userActivity.actions.length > 0 && (
                              <div>
                                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                  <BoltIcon className="w-4 h-4" />
                                  Actions Performed
                                </p>
                                <div className="space-y-2">
                                  {session.userActivity.actions.slice(0, 5).map((action, aidx) => {
                                    const ActionIcon = getActionIcon(action.type);
                                    return (
                                      <div
                                        key={`${action.timestamp}-${aidx}`}
                                        className="bg-white rounded-lg p-3 border border-gray-100 hover:border-blue-200 transition-colors"
                                      >
                                        <div className="flex items-start gap-3">
                                          <div className="p-1.5 bg-blue-50 rounded-md">
                                            <ActionIcon className="w-4 h-4 text-blue-600" />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                              <span className="text-sm font-medium text-gray-900">
                                                {formatActionType(action.type)}
                                              </span>
                                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                                {safeFormatDistanceToNow(action.timestamp, { addSuffix: true })}
                                              </span>
                                            </div>
                                            {action.topic && (
                                              <p className="text-xs text-gray-500 mt-0.5">
                                                Topic: <span className="font-mono bg-gray-100 px-1 rounded">{action.topic}</span>
                                              </p>
                                            )}
                                            {action.updateResults && action.updateResults.length > 0 && (
                                              <div className="mt-2 space-y-1">
                                                {action.updateResults.map((update, uidx) => (
                                                  <div key={uidx} className="text-xs flex items-center gap-2 bg-gray-50 rounded px-2 py-1">
                                                    <span className="text-gray-600 font-medium">{update.path}:</span>
                                                    <span className="text-red-500 line-through">{formatValue(update.oldValue)}</span>
                                                    <span className="text-gray-400">→</span>
                                                    <span className="text-green-600 font-medium">{formatValue(update.newValue)}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            {action.commandId && (
                                              <div className="mt-2 flex items-center gap-2">
                                                {action.ackStatus === 'acknowledged' ? (
                                                  <CheckCircleSolid className="w-4 h-4 text-green-500" />
                                                ) : action.ackStatus === 'failed' ? (
                                                  <XCircleIcon className="w-4 h-4 text-red-500" />
                                                ) : (
                                                  <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />
                                                )}
                                                <span className={`text-xs font-medium ${
                                                  action.ackStatus === 'acknowledged' ? 'text-green-600' :
                                                  action.ackStatus === 'failed' ? 'text-red-600' :
                                                  'text-yellow-600'
                                                }`}>
                                                  {action.ackStatus}
                                                </span>
                                                {action.responseTime && (
                                                  <span className="text-xs text-gray-400">
                                                    {action.responseTime}ms
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {session.userActivity.actions.length > 5 && (
                                    <p className="text-xs text-gray-500 text-center py-2">
                                      +{session.userActivity.actions.length - 5} more actions
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* View device button */}
                            <div className="mt-4 pt-3 border-t border-gray-100">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/dashboard/devices/${session.deviceSerial}`);
                                }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                              >
                                View Device
                                <ChevronRightIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setPage(prev => prev + 1)}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  Load More Sessions
                  <ChevronDownIcon className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}

        {/* Empty state */}
        {sessions.length === 0 && !loading && (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No activity recorded yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions
function formatDuration(milliseconds) {
  if (!milliseconds) return '0s';

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}

function formatValue(value) {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function isValidDate(date) {
  if (!date) return false;
  const d = new Date(date);
  return d instanceof Date && !isNaN(d);
}

function safeFormatDistanceToNow(date, options = {}) {
  if (!isValidDate(date)) return 'Unknown';
  try {
    return formatDistanceToNow(new Date(date), options);
  } catch {
    return 'Unknown';
  }
}

function safeFormat(date, formatStr) {
  if (!isValidDate(date)) return 'N/A';
  try {
    return format(new Date(date), formatStr);
  } catch {
    return 'N/A';
  }
}

export default UserSessionActivity;
